"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Globe2,
  Sparkles,
  Crown,
  Stethoscope,
  Brain,
  Building2,
  Target,
  Award,
  ArrowRight,
  Mic,
  BookOpen,
  CheckCircle2,
  Crosshair,
  Users,
  Compass,
  Briefcase,
  ShieldCheck,
  Eye,
  Layers,
  HeartPulse,
  Heart,
  Trophy,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { ArchitectureDiagramBlock } from "@/lib/blocks/architecture-diagram-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const TRIPLE_CENTER = {
  label: "Cross-Domain Integration",
  body: "The operating method that converges clinical precision, technological scale, and political sophistication into a single architecture.",
  icon: <Crown className="size-5" />,
  accent: "amber-gold" as const,
};

const TRIPLE_SATELLITES = [
  { label: "Medicine", body: "Clinical diagnostic discipline — the six principles that keep patients alive, transferred to the boardroom.", icon: <HeartPulse className="size-4" />, accent: "red" as const },
  { label: "Technology", body: "Operational scale — nine proprietary AI/ML platforms deployed across two hundred-plus surfaces and eighteen countries.", icon: <Layers className="size-4" />, accent: "cyan" as const },
  { label: "Politics", body: "Stakeholder sophistication — engagements with governments, royal families, and defense agencies across thirty-plus countries.", icon: <Globe2 className="size-4" />, accent: "emerald" as const },
];

const TRIPLE_CONNECTIONS = [
  { from: 0, to: 1, label: "Governs" },
  { from: 1, to: 2, label: "Enables" },
  { from: 2, to: 0, label: "Validates" },
];

const DOMAIN_SYNERGIES = [
  { title: "Clinical Discipline → Technology", body: "The diagnostic method that governs medicine — symptom identification, causal analysis, context assessment, intervention design — becomes the deployment protocol for sovereign-scale platforms.", icon: <Crosshair className="size-4" />, accent: "red" as const },
  { title: "Technology → Political Reach", body: "Nine proprietary platforms operating at ninety-nine-point-nine-nine-nine-nine percent uptime across eighteen countries become the infrastructure that delivers outcomes to over nine hundred million voters.", icon: <Globe2 className="size-4" />, accent: "cyan" as const },
  { title: "Political Sophistication → Clinical Outcomes", body: "The stakeholder navigation that produces fifteen out of eighteen campaign victories is the same discipline that produces clinical outcomes under sovereign-grade pressure.", icon: <Trophy className="size-4" />, accent: "emerald" as const },
  { title: "Medical Foundation → All Domains", body: "MBBS training in Gynaecology from Delhi provides the diagnostic foundation. The triage skill transfers from the delivery room to the boardroom. The do-no-harm assessment prevents value destruction across every domain.", icon: <HeartPulse className="size-4" />, accent: "purple" as const },
  { title: "Platform Engineering → Patient-Level Precision", body: "The same platform engineering discipline that coordinates eighteen countries also produces client-level precision in campaign targeting, threat assessment, and operational decision-making.", icon: <Layers className="size-4" />, accent: "amber-gold" as const },
  { title: "Sovereign Engagements → Cross-Domain Authority", body: "Governments, royal families, and defense agencies require the cross-domain authority to operate across all three simultaneously. No single-domain specialist can match the integration.", icon: <ShieldCheck className="size-4" />, accent: "denim" as const },
];

const FAQ_ITEMS = [
  { q: "What is \"The Triple Threat\" about?", a: "The signature keynote on cross-domain integration — medicine, technology, and politics as one operating method. The argument is that the most demanding problems in the world are not single-domain problems, and the operators who solve them are not single-domain specialists. Sovereign-scale outcomes require the integration of clinical precision, technological scale, and political sophistication." },
  { q: "How is this different from multi-disciplinary keynotes?", a: "Most multi-disciplinary keynotes present three perspectives sequentially. This keynote presents one operating method that has been developed across three domains simultaneously. The integration is structural, not additive. The same discipline that produces clinical outcomes produces technological outcomes produces political outcomes." },
  { q: "What does cross-domain integration look like in practice?", a: "The clinical method governs platform deployment. The platform infrastructure delivers political outcomes. The political sophistication validates the clinical approach. The three domains reinforce each other — producing outcomes that no single-domain specialist can replicate." },
  { q: "How long is the standard keynote?", a: "Fifty to sixty-five minutes for the standard keynote. Fireside chat runs thirty to forty-five minutes. Workshop and masterclass runs two to four hours. Executive briefing runs sixty to ninety minutes. All formats are available virtually and in person." },
  { q: "Who is the ideal audience for this keynote?", a: "Cross-disciplinary leaders navigating problems that span multiple domains — AI governance forums, digital transformation conferences, health technology summits, sovereign operations gatherings, defense and security conferences, and Fortune-scale organizations facing complex cross-domain challenges." },
  { q: "What evidence supports the cross-domain argument?", a: "Fifteen out of eighteen political campaigns won. Over nine hundred million voters reached. One-hundred-percent client satisfaction. Ninety-nine-point-nine-nine-nine-nine percent operational uptime. Zero security incidents across fifteen-plus years. These outcomes were produced by the integration of medicine, technology, and politics — not by any single domain in isolation." },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const THREE_DIMENSIONS = [
  {
    icon: Stethoscope,
    title: "Medicine",
    body: "Provides the method. Clinical training instills a discipline that business schools do not teach: evidence over intuition, protocol under pressure, triage when everything is urgent, and accountability not for effort but for outcome. The six principles form the actual operating system.",
    accent: "red",
    metric: "MBBS",
    metricLabel: "Gynaecology · Delhi",
  },
  {
    icon: Brain,
    title: "Technology",
    body: "Provides the scale. The integrated platform ecosystem, built in-house over fifteen-plus years, processes millions of data points, monitors two hundred-plus digital platforms, and orchestrates operations across continents in under an hour. Zero third-party dependencies.",
    accent: "cyan",
    metric: "60+",
    metricLabel: "Integrated services",
  },
  {
    icon: Globe2,
    title: "Politics",
    body: "Provides the stakes. When the client is a sovereign government, a royal household, a defense ministry, or a political leader facing an existential contest, the work is not theoretical. Outcomes shape nations, economies, and the lives of millions.",
    accent: "amber-gold",
    metric: "900M",
    metricLabel: "Voters reached",
  },
];

const EVIDENCE = [
  { metric: "15/18", suffix: "", label: "Campaign Victories", sub: "Across sovereign engagements", accent: "amber-gold" },
  { metric: "900", suffix: "M+", label: "Voters Reached", sub: "22 official languages", accent: "red" },
  { metric: "100", suffix: "%", label: "Client Satisfaction", sub: "300+ elite clients", accent: "purple" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years sovereign-grade", accent: "cyan" },
];

const TAKEAWAYS = [
  { title: "The most demanding problems are cross-domain problems.", body: "AI governance, sovereign digital transformation, national security — these do not respect disciplinary boundaries. Single-domain specialists will reach their ceiling. Cross-domain integrators will define the future.", accent: "amber-gold" },
  { title: "Clinical discipline governs all three domains.", body: "The operating method from medicine is not a metaphor for technology and politics. It is the actual operating system. The same discipline that keeps patients alive produces outcomes at sovereign scale.", accent: "red" },
  { title: "The integration is the competitive advantage.", body: "No competitor can replicate a closed-loop system where multiple integrated platforms, eighteen countries of operational experience, and three integrated domains produce compound outcomes.", accent: "purple" },
  { title: "Technology without clinical discipline produces unintended consequences.", body: "Technology governed by clinical discipline produces systems that diagnose before deploying, assess before implementing, and own the outcome rather than the activity.", accent: "cyan" },
  { title: "Outcomes are the only metric that matters.", body: "Fifteen out of eighteen political campaigns won. Over nine hundred million voters reached. One-hundred-percent client satisfaction. Zero security incidents. These are not aspirations. They are verified results.", accent: "emerald" },
];

const AUDIENCE = [
  { icon: Brain, title: "Technology Conferences", body: "Events focused on AI governance, sovereign digital transformation, cybersecurity, and enterprise technology architecture benefit from the cross-domain perspective.", accent: "amber-gold" },
  { icon: Stethoscope, title: "Healthcare Innovation", body: "Events exploring the intersection of healthcare, technology, and governance benefit from the physician&apos;s perspective on clinical method and technological capability.", accent: "red" },
  { icon: Building2, title: "Government & Sovereign", body: "Events addressing governance transformation, digital sovereignty, national security, and public service delivery benefit from the integration of clinical precision, technology, and political sophistication.", accent: "purple" },
  { icon: ShieldCheck, title: "AI Governance Summits", body: "Events addressing governance of artificial intelligence at national and international scale benefit from the cross-domain perspective connecting clinical rigor to technology governance.", accent: "cyan" },
  { icon: Eye, title: "Defense & Security", body: "Events addressing national security, intelligence operations, and defense coordination benefit from the integration of clinical precision with technological capability under political-grade stakes.", accent: "emerald" },
  { icon: Users, title: "Interdisciplinary Programs", body: "Academic institutions seeking to expose students and executives to the cross-domain operating method — where the most consequential work is done at the intersection of disciplines.", accent: "denim" },
];

const FORMATS = [
  { label: "Keynote Address", duration: "50–65 min", body: "Concentrated cross-domain insight, tailored to the audience and the event theme. The primary delivery mode.", accent: "amber-gold" },
  { label: "Fireside Chat", duration: "30–45 min", body: "Conversational depth for intimate leadership gatherings and interdisciplinary forums. Effective for audiences under two hundred.", accent: "red" },
  { label: "Panel Participation", duration: "20–30 min", body: "Cross-domain perspective contributed to multi-speaker discussions alongside single-domain specialists.", accent: "purple" },
  { label: "Workshop / Masterclass", duration: "2–4 hrs", body: "Hands-on cross-domain training applying the integrated operating method to specific organizational challenges.", accent: "cyan" },
  { label: "Executive Briefing", duration: "60–90 min", body: "Private session for leadership teams navigating cross-domain complexity. The most confidential format.", accent: "emerald" },
  { label: "Virtual / Remote", duration: "All formats", body: "All formats available for virtual events with the same cross-domain authority and engagement quality.", accent: "denim" },
];

const GAINS = [
  "The diagnostic approach to cross-domain problems — assessing clinical, technological, and political dimensions before prescribing",
  "Triage at the intersection — ranking competing priorities when the priorities span different domains",
  "Protocol-driven cross-domain operations — the six-stage engagement methodology that governs sovereign-scale integration",
  "Outcome accountability across domains — tracing outcomes to their cross-domain sources to prevent blame-shifting",
  "The integration discipline — maintaining integration under pressure when the urgency to specialize is greatest",
  "Cross-domain frameworks — applied to the audience&apos;s specific organizational and industry context",
];

export default function TheTripleThreatPage() {
  return (
    <>
      <PageHero
        icon={Globe2}
        overline="Signature Keynote"
        overlineClassName="text-amber-gold"
        title={
          <>
            Three Domains. <span className="text-gradient-premium">One Operating Method.</span>
          </>
        }
        subtitle="The Triple Threat is the only presentation that demonstrates how medicine, technology, and politics function as a single integrated operating method. Clinical precision meets sovereign-scale technology under political-grade stakes. The audience witnesses the integration that produces outcomes no single-domain specialist can replicate."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Speaking", href: "/speaking/" }, { label: "The Triple Threat" }]}
        badge="Medicine · Technology · Politics"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Abstract</span>
          </div>
          <h2 className="text-display-md mb-5">The intersection is where complex problems are solved.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            Most leaders specialize in a single domain. The problems that define the next decade — AI governance, sovereign digital transformation, national security in the age of artificial intelligence, governance delivery at scale — do not respect disciplinary boundaries. A governance crisis is simultaneously a technology problem, a political problem, and a human problem.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The leaders who will solve these problems are not the ones who master one domain. They are the ones who integrate three. The MBBS in Gynaecology from Delhi provides the diagnostic framework. The decade of operational experience across eighteen countries provides the operational scale. The political exposure provides the stakeholder sophistication. The integration is the operating method that produces outcomes no single-domain specialist can replicate.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-4`}>
            <Trophy className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Evidence</span>
          </div>
          <h2 className="text-display-md mb-4">The operational case.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four outcomes that demonstrate the cross-domain integration. Not projections. Not aspirations. Verified results from sovereign-scale operations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {EVIDENCE.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Layers className="size-3.5 text-red" />
            <span className="text-overline text-red">The Three Dimensions</span>
          </div>
          <h2 className="text-display-md mb-4">Three domains, one method.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Most leaders specialize in a single domain. The career operates across three — and the intersection is where the value is unmatched. Each domain amplifies the others.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {THREE_DIMENSIONS.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <d.icon className="size-6 text-white" />
                  </div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <h3 className={`text-display-sm ${a.text}`}>{d.metric}</h3>
                    <span className="text-caption text-muted-foreground">{d.metricLabel}</span>
                  </div>
                  <h4 className="text-heading-md mb-2 mt-3">{d.title}</h4>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{d.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Target className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">The Integration</span>
            </div>
            <h2 className="text-display-md mb-4">How all three domains work together.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The integration is the synthesis that makes the entire operating method greater than the sum of its parts. Medicine, technology, and politics are not three separate capabilities deployed in parallel. They are three dimensions of a single operating method — each amplifying the others, each constrained by the others, each producing outcomes the other two cannot produce alone.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The clinical method governs the framework: diagnose the situation, assess the threat, secure the infrastructure, implement the strategy, deliver the outcome. Each pillar feeds the others. Each capability amplifies the others. The integration is not a design document. It is operational reality — validated across eighteen countries and over three hundred elite clients.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The integration is the moat. No competitor has spent years operating at the intersection of three domains, building the infrastructure refined through hundreds of campaigns, and assembling multidisciplinary teams that span political analysts, researchers, designers, developers, and strategists. The integration is not replicable without the decade of investment that produced it.
            </p>
          </div>
          <div className="space-y-3">
            {[
              { title: "Closed-Loop Intelligence", body: "Intelligence flows through the integration to inform strategy, content is deployed, ground truth is validated, crises are managed. Each engagement makes the next one smarter.", accent: "amber-gold" },
              { title: "Compound Capability", body: "When three pillars activate simultaneously for crisis response, the speed advantage is transformative. The integration produces outcomes that single-pillar approaches cannot replicate.", accent: "red" },
              { title: "Stakeholder Integration", body: "The clinical method ensures diagnostic rigor, the technology infrastructure ensures operational scale, the political sophistication ensures outcomes navigate the realities of sovereign institutions.", accent: "purple" },
              { title: "Domain Amplification", body: "Medicine&apos;s discipline amplifies technology. Technology&apos;s scale amplifies politics. Politics&apos; stakes amplify medicine. The integration is the multiplier.", accent: "cyan" },
              { title: "Sovereign-Scale Verification", body: "Validated across eighteen countries, sixty-plus services, over three hundred elite clients. The integration is not a framework. It is the operating reality.", accent: "emerald" },
            ].map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <span className="text-caption font-bold text-white">{i + 1}</span>
                    </div>
                    <div>
                      <h3 className={`text-heading-sm mb-1 ${a.text}`}>{p.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Key Takeaways</span>
          </div>
          <h2 className="text-display-md mb-4">Five insights for cross-domain leaders.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The minimum viable operating model for cross-domain integration. Specific, implementable actions for leaders navigating complexity at the highest levels.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3 max-w-5xl mx-auto">
          {TAKEAWAYS.map((t, i) => {
            const accents = ["amber-gold", "red", "purple", "cyan", "emerald"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-sm mb-1 ${a.text}`}>{t.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Users className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Who This Keynote Serves</span>
          </div>
          <h2 className="text-display-md mb-4">Ideal audience.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The keynote is designed for audiences operating at the intersection of multiple domains — where complexity exceeds any single discipline and where the integration of medicine, technology, and politics produces outcomes that single-domain approaches cannot.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCE.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-sm mb-1.5 ${ac.text}`} dangerouslySetInnerHTML={{ __html: a.title }} />
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Briefcase className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">Format Options</span>
            </div>
            <h2 className="text-display-md mb-4">Flexibility for every event.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Six delivery formats, each calibrated to the audience&apos;s cross-domain challenges and the outcomes sought. The customization is structural, with the emphasis shifting based on the audience&apos;s industry.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FORMATS.map((f, i) => {
              const a = ACCENT[f.accent];
              return (
                <motion.div
                  key={f.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className={`text-heading-sm ${a.text}`}>{f.label}</h3>
                    <span className={`text-caption font-bold ${a.text}`}>{f.duration}</span>
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: f.body }} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Globe2 className="size-10 text-amber-gold mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Book this keynote.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The Triple Threat is the cross-domain keynote in the portfolio. The only presentation that demonstrates how medicine, technology, and politics function as a single integrated operating method. The integration that produces outcomes no single-domain specialist can replicate.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Book the Keynote
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/speaking/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                All Speaking Topics
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-5`}>
            <Layers className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Signature Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">The Triple Intersection Map.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The three domains do not operate in parallel. They converge. The center — cross-domain integration — is the operating method that no single-domain specialist can replicate. The three satellites — medicine, technology, politics — are the dimensions through which the method is practiced. The connections between them are causal, not decorative: clinical discipline governs technology deployment, technological scale enables political reach, and political sophistication validates the clinical foundation.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The map is the architecture that produces sovereign-scale outcomes. Each domain is necessary. None is sufficient. The integration — the synthesis of clinical precision, operational scale, and stakeholder sophistication — is the source of the competitive advantage that has served eighteen countries, nine proprietary platforms, and over three hundred elite clients.
          </p>
        </div>
        <div className="mt-12">
          <ArchitectureDiagramBlock
            center={TRIPLE_CENTER}
            satellites={TRIPLE_SATELLITES}
            connections={TRIPLE_CONNECTIONS}
          />
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Globe2 className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">How the Domains Reinforce Each Other</span>
          </div>
          <h2 className="text-display-md mb-4">Domain synergies that compound.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each domain reinforces the others. The clinical discipline that governs medicine becomes the deployment protocol for technology. The technological scale that serves eighteen countries delivers political outcomes at scale. The political sophistication that navigates sovereign engagements validates the clinical approach. The loop closes on itself.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DOMAIN_SYNERGIES.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    {d.icon}
                  </div>
                  <h3 className={`text-heading-sm ${a.text}`}>{d.title}</h3>
                </div>
                <p className="text-caption text-muted-foreground leading-relaxed">{d.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">The Problem This Keynote Solves</span>
          </div>
          <h2 className="text-display-md mb-4">The siloed specialist cannot solve the cross-domain problem.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The most consequential problems of the current era are not single-domain problems. AI governance is simultaneously a technology problem, a political problem, and a human problem. Sovereign digital transformation is simultaneously a clinical discipline problem, a technological infrastructure problem, and a political mandate problem. Health technology deployment is simultaneously a medical practice problem, an engineering problem, and a regulatory problem. The audience&apos;s challenges are already cross-domain. The single-domain specialist cannot address them.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The root cause is the structure of professional education. Specialists are trained in single domains. They produce single-domain solutions. The problems that matter — the ones that determine national outcomes, regulatory frameworks, and operational survivability — are cross-domain. The gap between how problems are structured and how operators are trained is the gap this keynote closes.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The solution is the cross-domain operator — the practitioner who has been trained across domains, who has operated at the intersection, and who has produced outcomes that no single-domain specialist can replicate. The Triple Threat keynote presents the operating method of the cross-domain operator, demonstrates its application through verified sovereign-scale outcomes, and equips the audience with the framework to develop cross-domain capability within their own organizations. The integration is the competitive advantage.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <AccordionBlock
          items={FAQ_ITEMS}
          eyebrow="Frequently Asked Questions"
          title="What decision-makers ask before booking."
          description="The questions that surface most often in preliminary conversations. Each answer reflects the operating reality, not the marketing narrative."
        />
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "How Excellence Actually Runs", description: "The operating system that cross-domain integration demands — protocols, checklists, reviews, accountability.", href: "/speaking/how-excellence-actually-runs/", icon: Award, accent: "red" },
          { title: "Leadership as Diagnosis", description: "The clinical method that governs the integration — the six skills that scale across domains.", href: "/speaking/leadership-as-diagnosis/", icon: Brain, accent: "purple" },
          { title: "Build in the Dark", description: "The discretion discipline that cross-domain operations require — building quietly, measuring by outcomes.", href: "/speaking/build-in-the-dark/", icon: Eye, accent: "denim" },
          { title: "Women Breaking Barriers", description: "The individual operating reality of leading at the intersection — the discipline that makes it possible.", href: "/speaking/women-breaking-barriers/", icon: Heart, accent: "coral" },
        ]}
      />
    </>
  );
}
